(ns ncr.fragment-types.widgets
  (:require
   [clojure.string :as str]
   [uix.core.alpha :as uix]
   [ncr.fragment.common :as fc]
   [ncr.etc.rhf :as rhf]
   [ncr.etc.pf :as pf]
   [ncr.widgets.core :as w]
   [ncr.widgets.pf :as wpf]
   [ncr.fragment.attachments-backend :as ba]
   [cljs.core.async :refer-macros [go] :refer [<!]]
   [ncr.etc.clipboard :as clipboard]
   [ncr.etc.util :as u]
   [ncr.etc.pubsub :as ps]
   [ncr.etc.modal :as m]
   [ncr.etc.alert :as a]
   [ncr.backend :as b]))

(defn- c-rename-attachment-dialog [props initial fragment-name]
  (let
    [{:keys [submit register-pf set-value] :as form}
     (rhf/use-form {:shouldFocusError false :default-values {:value initial}})
     *clipboard (uix/state nil)

     ref (uix/ref)
     modal-id :rename-attachment-modal

     suggestions (uix/memo
                   (fn []
                     (concat
                       (when-let [clipboard (fc/sanitize-suggestion @*clipboard)] [clipboard])
                       (when-let [filename (-> fragment-name str/trim
                                             (str "." (or (second (re-find #"([^.]*)$" initial)) ""))
                                             fc/sanitize-suggestion)]
                         [filename])))
                   [fragment-name initial @*clipboard])]

    (clipboard/use-read
      #(let [s (fc/sanitize-clipboard-suggestion %)]
         (when (and s (> (count s) 3)) (reset! *clipboard s))))

    (m/use-submit-form modal-id ref submit)

    (u/without [label] props
      [m/c-modal
       (w/merge-props
         {:variant :medium
          :id modal-id
          :ref ref
          :actions
          [[pf/c-button
            {:variant :primary
             :on-click submit}
            "Ok"]
           [pf/c-button
            {:variant :link :on-click #(m/close! nil)} "Cancel"]]}
         props)

       [pf/c-form {:on-submit submit}
        (let [id (uix/memo #(gensym) [])]
          [rhf/c-form-group
           {:field-id id
            :label label
            :is-required true
            :class :ncr-fragment-attachment-name-control}
           :value form
           [:<>
            [pf/c-text-input (merge (register-pf :value {:required true}) {:id id})]
            (for [suggestion suggestions]
              [pf/c-chip-group {:key suggestion :class :ncr-recent}
               [wpf/c-chip suggestion
                (fn [^js e]
                  (if (= 2 (.-detail e))
                    (submit e)
                    (set-value :value suggestion {:shouldDirty true :shouldTouch true})))]])]])]])))

(defn !attachment-rename-operation [reference-id attachment-id new-name]
  (a/!operation {:operation-title "Rename Attachment" :success-title "Attachment Renamed" :body new-name}
    (b/!mutation
      [:attachment_rename
       {:reference reference-id
        :attachment attachment-id
        :name new-name}
       [:id]])))

(defn !attachment-rename [reference-id attachment fragment-name]
  (go
    (if-let
      [{new-name :value}
       (<!
         (m/!modal #'c-rename-attachment-dialog
           {:title "Rename Attachment" :label "Name"}
           (get attachment :fileName)
           fragment-name))]
      (<! (!attachment-rename-operation reference-id (get attachment :id) new-name))
      [:error :cancel])))

(defn !attachment-set-deleted [reference-id attachments deleted]
  (go
    (<!
      (a/!operation
        (let
          [count (count attachments)
           single? (= count 1)
           single-file-name
           (when single?
             (-> attachments first :fileName))]
          (if deleted
            (if single?
              {:operation-title "Delete File" :success-title "File Deleted" :body single-file-name}
              {:operation-title "Delete Files" :success-title (u/format "%d Files Deleted" count)})

            (if single?
              {:operation-title "Restore File" :success-title "File Restored" :body single-file-name}
              {:operation-title "Restore Files" :success-title (u/format "%d Files Restored" count)})))
        (b/!multi-mutation
          (map
            (fn [{:keys [id]}]
              [id
               [:attachment_set_deleted
                {:reference reference-id
                 :attachment id
                 :deleted deleted}
                [:id]]])
            attachments))))))

(defn c-toolbar [fragment-name {:keys [id] :as attachment} close attachments set-preview type-specific-tools]
  [:div
   {:class :ncr-c-attachment-viewer__toolbar-container}
   [pf/c-toolbar
    (let [reference-id (-> attachments first :reference-id)]
      [pf/c-toolbar-content
       [pf/c-toolbar-group {:class :pf-m-icon-button-group}
        [w/c-toolbar-button
         {:icon :printer-outline
          :tooltip "Print"
          :on-click
          (fn [e]
            (.stopPropagation e)
            (ba/print! reference-id id))}]
        [w/c-toolbar-button
         {:icon :file-download-outline
          :tooltip "Download"
          :on-click
          (fn [e]
            (.stopPropagation e)
            (ba/download! reference-id id :ATTACHMENT))}]
        [w/c-toolbar-button
         {:icon :pencil-outline
          :tooltip "Rename"
          :on-click
          (fn [e]
            (.stopPropagation e)
            (go
              (u/when-ok [_ (<! (!attachment-rename reference-id attachment fragment-name))]
                (ps/pub! [:fragment/load false]))))}]]

       type-specific-tools

       [pf/c-toolbar-group
        {:variant :icon-button-group
         :class :ncr-c-attachment-viewer__right-toolbar}
        (let
          [options
           (map
             (fn [{:keys [deleted] :as attachment}]
               (merge attachment
                 {:class [:pf-u-max-width (when deleted :ncr-m-deleted)]}))
             attachments)]
          [pf/c-toolbar-item {:class :ncr-c-attachment-viewer__attachment-selector}
           ^{:key id}
           [pf/c-tooltip {:content [:span "Attachments"]}
            [wpf/c-pf-select
             {:variant :single
              :label-fn :fileName
              :class
              [:ncr-c-attachment-viewer__navigation
               :pf-u-max-width
               (when (->> attachments (some #(when (= id (:id %)) %)) :deleted)
                 :ncr-m-deleted)]
              :position :right
              :selections (some #(when (= id (:id %)) %) options)
              :on-select
              (fn [_ {:keys [id mimeType]} _]
                (set-preview id mimeType))}
             (sort-by :fileName options)]]])
        [w/c-toolbar-button
         {:toolbar-item-props {:class :ncr-c-attachment-viewer__close}
          :variant :plain
          :icon :close
          :on-click close}]]])]])

(defn- c-error [error loading? reload]
  [pf/c-empty-state {:class [:ncr-c-attachment-viewer__error]}
   [pf/c-empty-state-icon
    {:variant :container
     :component [w/c-mdi-icon {:icon :alert-circle-outline}]}]
   [pf/c-title {:heading-level :h4 :size :lg} "Error loading file"]
   [pf/c-empty-state-body error]
   [pf/c-button
    {:variant :primary
     :on-click reload
     :is-disabled loading?}
    "Reload"]])

(defn- c-loading []
  [pf/c-empty-state
   [pf/c-empty-state-icon
    {:variant :container
     :component [pf/c-spinner]}]
   [pf/c-title {:heading-level :h4 :size :lg} "Loading file"]])

(defn c-content [error loading? reload body]
  [:div.ncr-c-attachment-viewer__body {:class [:pf-m-grow :pf-u-mt-none]}
   (when loading?
     [c-loading])

   (when (and error (not loading?))
     [c-error error loading? reload])

   [:div.ncr-c-attachment-viewer__content
    (merge {} (when (or error loading?) {:style {:display :none}}))
    body]])
